import { useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { MapPin } from "@phosphor-icons/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default function DeviceMap({ events }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up previous map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const locations = events.filter((e) => e.latitude && e.longitude);
    if (locations.length === 0) return;

    // Delay initialization to ensure container is rendered
    const timer = setTimeout(() => {
      if (!mapRef.current) return;

      const center = [locations[0].latitude, locations[0].longitude];
      const map = L.map(mapRef.current, {
        zoomControl: false,
        attributionControl: false,
      }).setView(center, 14);

      L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        { maxZoom: 19 }
      ).addTo(map);

      // Group locations by device
      const deviceGroups = {};
      locations.forEach((loc) => {
        const name = loc.device_name || "Unknown";
        if (!deviceGroups[name]) deviceGroups[name] = [];
        deviceGroups[name].push(loc);
      });

      const deviceColors = ["#00DCC2", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];
      const deviceNames = Object.keys(deviceGroups);

      deviceNames.forEach((name, idx) => {
        const color = deviceColors[idx % deviceColors.length];
        const locs = deviceGroups[name];
        const firstLoc = locs[0];
        const pos = [firstLoc.latitude, firstLoc.longitude];

        // Main marker
        L.circleMarker(pos, {
          radius: 7, fillColor: color, color: color,
          weight: 2, fillOpacity: 0.9,
        }).addTo(map).bindPopup(
          `<div style="font-family: 'IBM Plex Mono', monospace; font-size: 11px; color: #E0E0E0; background: #252525; padding: 6px; max-width: 220px;">
            <b style="color:${color}">${name}</b><br/>
            <span style="color: #A0A0A0;">${firstLoc.address || "Unknown"}</span><br/>
            <span style="color: ${color};">${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}</span>
          </div>`
        );

        // Pulse ring
        L.circleMarker(pos, {
          radius: 16, fillColor: color, color: color,
          weight: 1, fillOpacity: 0.1,
        }).addTo(map);
      });

      // Fit bounds if multiple device locations
      const seen = new Set();
      const uniqueCoords = [];
      locations.forEach((l) => {
        const key = `${l.latitude.toFixed(4)},${l.longitude.toFixed(4)}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueCoords.push([l.latitude, l.longitude]);
        }
      });

      if (uniqueCoords.length > 1) {
        try {
          map.fitBounds(L.latLngBounds(uniqueCoords), { padding: [40, 40], maxZoom: 15 });
        } catch (e) { /* ignore */ }
      }

      map.invalidateSize();
      mapInstanceRef.current = map;
    }, 100);

    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [events]);

  const hasLocations = events.some((e) => e.latitude && e.longitude);

  return (
    <Card
      data-testid="device-map"
      className="bg-zinc-900 border-zinc-800 rounded-sm p-4"
    >
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
        Device Location
      </h3>
      {hasLocations ? (
        <div
          ref={mapRef}
          className="h-[220px] rounded-sm overflow-hidden"
          style={{ background: "#1A1A1A" }}
          data-testid="map-container"
        />
      ) : (
        <div className="h-[220px] flex items-center justify-center">
          <MapPin
            size={36}
            weight="duotone"
            className="text-zinc-700 mr-2"
          />
          <p className="text-xs text-zinc-600">No location data available</p>
        </div>
      )}
    </Card>
  );
}
